import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Typography, Space, Button, DatePicker, message, Spin, Tabs } from 'antd';
import { DownloadOutlined, BarChartOutlined, TrophyOutlined, HeatMapOutlined } from '@ant-design/icons';
import { GoogleMap, useJsApiLoader, HeatmapLayer } from '@react-google-maps/api';
import API from '../api';

const MAPS_KEY = 'AIzaSyBpuOwZJc2U2S8oRdwQKLuuUV2uj2-i8zw';
const JHB = { lat: -26.2041, lng: 28.0473 };
const MAPS_LIBS: ['visualization'] = ['visualization'];
const HEAT_CONTAINER = { height: 450, borderRadius: 8 };

const MOCK_HEAT_POINTS = [
  { lat: -26.2041, lng: 28.0473, weight: 8 },
  { lat: -26.1076, lng: 28.0567, weight: 10 },
  { lat: -26.3215, lng: 27.9244, weight: 5 },
  { lat: -25.7479, lng: 28.2293, weight: 7 },
  { lat: -26.1896, lng: 28.1567, weight: 4 },
  { lat: -25.9967, lng: 28.1283, weight: 6 },
  { lat: -26.1634, lng: 27.8724, weight: 3 },
  { lat: -25.9956, lng: 28.2265, weight: 9 },
  { lat: -26.2327, lng: 28.1063, weight: 5 },
  { lat: -25.8601, lng: 28.1882, weight: 6 },
];

// A27: CSV download utility
const downloadCSV = (rows: any[], filename: string) => {
  if (!rows.length) { message.warning('No data to export'); return; }
  const headers = Object.keys(rows[0]);
  const escape = (val: any) => {
    const str = String(val ?? '').replace(/"/g, '""');
    return /[,"\n]/.test(str) ? `"${str}"` : str;
  };
  const csv = [headers.join(','), ...rows.map(row => headers.map(h => escape(row[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// A24: Supply vs Demand heatmap
const SupplyDemandMap = () => {
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY, libraries: MAPS_LIBS });
  const [rawPoints, setRawPoints] = useState<{ lat: number; lng: number; weight?: number }[]>([]);

  useEffect(() => {
    API.get('/analytics/heatmap')
      .then(res => setRawPoints(res.data))
      .catch(() => setRawPoints(MOCK_HEAT_POINTS));
  }, []);

  if (!isLoaded) {
    return (
      <div style={{ height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', borderRadius: 8 }}>
        <Spin tip="Loading map…" />
      </div>
    );
  }

  const heatData = rawPoints.map(p => ({
    location: new window.google.maps.LatLng(p.lat, p.lng),
    weight: p.weight ?? 1,
  }));

  return (
    <GoogleMap mapContainerStyle={HEAT_CONTAINER} center={JHB} zoom={10}>
      <HeatmapLayer data={heatData} options={{ opacity: 0.75, radius: 25, dissipating: true }} />
    </GoogleMap>
  );
};

const { Title } = Typography;
const { RangePicker } = DatePicker;

// A25: Category Performance — accepts onDataLoaded callback for A27 export
const CategoryPerformance = ({ onDataLoaded }: { onDataLoaded: (data: any[]) => void }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get('/analytics/categories')
      .then(res => { setData(res.data); onDataLoaded(res.data); })
      .catch(() => {
        const mock = [
          { category: 'Plumbing', requestVolume: 145, completionRate: 92, avgRating: 4.8, avgValue: 1250, disputeRate: 1.2 },
          { category: 'Cleaning', requestVolume: 320, completionRate: 98, avgRating: 4.9, avgValue: 450, disputeRate: 0.5 },
          { category: 'Electrical', requestVolume: 98, completionRate: 85, avgRating: 4.5, avgValue: 1800, disputeRate: 3.1 },
          { category: 'Handyman', requestVolume: 210, completionRate: 89, avgRating: 4.6, avgValue: 850, disputeRate: 2.0 },
        ];
        setData(mock);
        onDataLoaded(mock);
      })
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Category', dataIndex: 'category', sorter: (a: any, b: any) => a.category.localeCompare(b.category) },
    { title: 'Requests', dataIndex: 'requestVolume', sorter: (a: any, b: any) => a.requestVolume - b.requestVolume },
    { title: 'Completion Rate', dataIndex: 'completionRate', render: (v: number) => `${v}%`, sorter: (a: any, b: any) => a.completionRate - b.completionRate },
    { title: 'Avg Rating', dataIndex: 'avgRating', render: (v: number) => `★ ${v.toFixed(1)}`, sorter: (a: any, b: any) => a.avgRating - b.avgRating },
    { title: 'Avg Job Value', dataIndex: 'avgValue', render: (v: number) => `R${v.toFixed(2)}`, sorter: (a: any, b: any) => a.avgValue - b.avgValue },
    {
      title: 'Dispute Rate', dataIndex: 'disputeRate',
      render: (v: number) => <span style={{ color: v > 2 ? 'red' : 'inherit' }}>{v}%</span>,
      sorter: (a: any, b: any) => a.disputeRate - b.disputeRate
    },
  ];

  return <Table columns={columns} dataSource={data} rowKey="category" loading={loading} pagination={false} />;
};

// A26: Provider Leaderboard — accepts onDataLoaded callback for A27 export
const ProviderLeaderboard = ({ onDataLoaded }: { onDataLoaded: (data: any[]) => void }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.get('/analytics/leaderboard')
      .then(res => { setData(res.data); onDataLoaded(res.data); })
      .catch(() => {
        const mock = [
          { id: 14, name: 'John Plumber', jobsCompleted: 85, earnings: 45600, avgRating: 4.9, responseTime: '5m' },
          { id: 22, name: 'Sarah Cleaners', jobsCompleted: 142, earnings: 52000, avgRating: 5.0, responseTime: '2m' },
          { id: 8, name: 'Mike Electric', jobsCompleted: 67, earnings: 38500, avgRating: 4.7, responseTime: '15m' },
          { id: 45, name: 'Tim Handyman', jobsCompleted: 94, earnings: 41200, avgRating: 4.8, responseTime: '8m' },
        ];
        setData(mock);
        onDataLoaded(mock);
      })
      .finally(() => setLoading(false));
  }, []);

  const columns = [
    { title: 'Rank', key: 'rank', render: (_: any, __: any, index: number) => index + 1, width: 60 },
    { title: 'Provider Name', dataIndex: 'name' },
    { title: 'Jobs Completed', dataIndex: 'jobsCompleted', sorter: (a: any, b: any) => a.jobsCompleted - b.jobsCompleted },
    { title: 'Earnings (Total)', dataIndex: 'earnings', render: (v: number) => `R${v.toFixed(2)}`, sorter: (a: any, b: any) => a.earnings - b.earnings },
    { title: 'Avg Rating', dataIndex: 'avgRating', render: (v: number) => `★ ${v.toFixed(1)}`, sorter: (a: any, b: any) => a.avgRating - b.avgRating },
    { title: 'Avg Response', dataIndex: 'responseTime' },
  ];

  return <Table columns={columns} dataSource={data} rowKey="id" loading={loading} pagination={false} />;
};

export default function Analytics() {
  // A27: hold data lifted from child components for export
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [leaderboardData, setLeaderboardData] = useState<any[]>([]);

  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 24, justifyContent: 'space-between', width: '100%' }}>
        <Title level={2} style={{ margin: 0 }}>Analytics & Reports</Title>
        <Space>
          <RangePicker />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              const combined = [
                ...categoryData.map(r => ({ sheet: 'categories', ...r })),
                ...leaderboardData.map(r => ({ sheet: 'leaderboard', ...r })),
              ];
              downloadCSV(combined, `analytics-full-${today}.csv`);
            }}
          >
            Export Full Dataset
          </Button>
        </Space>
      </Space>

      <Tabs defaultActiveKey="categories">
        <Tabs.TabPane tab={<span><BarChartOutlined />Category Performance</span>} key="categories">
          <Card
            title="Category Performance Reports"
            extra={
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadCSV(categoryData, `category-performance-${today}.csv`)}
              >
                Export CSV
              </Button>
            }
          >
            <CategoryPerformance onDataLoaded={setCategoryData} />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab={<span><TrophyOutlined />Provider Leaderboard</span>} key="leaderboard">
          <Card
            title="Top 20 Providers"
            extra={
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => downloadCSV(leaderboardData, `provider-leaderboard-${today}.csv`)}
              >
                Export CSV
              </Button>
            }
          >
            <ProviderLeaderboard onDataLoaded={setLeaderboardData} />
          </Card>
        </Tabs.TabPane>

        <Tabs.TabPane tab={<span><HeatMapOutlined />Supply / Demand Map</span>} key="heatmap">
          <Card
            title="Supply vs Demand Heatmap"
            extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>Booking demand density — Gauteng region</Typography.Text>}
          >
            <SupplyDemandMap />
          </Card>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
